import { useEffect, useMemo, useState } from "react";
import { Play } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { ToolInvite } from "@/components/tools/ToolFinish";
import { resetTrack, track } from "@/lib/toolTrack";

const WRITE_SECONDS = 120;

const Writing = ({ text, setText, onDone }) => {
  const [left, setLeft] = useState(WRITE_SECONDS);
  useEffect(() => {
    const id = setInterval(() => setLeft((l) => Math.max(0, l - 1)), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <div data-testid="tool-worry-write">
      <div className="flex justify-between items-center text-sm text-mm-muted mb-2"><span>One worry per line. Don't filter, just write.</span><span className="tabular-nums font-semibold" data-testid="tool-worry-timer">{Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}</span></div>
      <textarea autoFocus className="mm-input min-h-[220px]" value={text} onChange={(e) => setText(e.target.value)} placeholder={"The deadline on Friday\nWhat they meant by that message\nMoney this month"} data-testid="tool-worry-input" />
      <button onClick={onDone} disabled={!text.trim()} className="mm-btn mt-3" data-testid="tool-worry-done">{left ? "I'm done" : "Time's up, sort them"}</button>
    </div>
  );
};

const Sort = ({ items, mine, toggle, onDone }) => (
  <div data-testid="tool-worry-sort">
    <p className="font-semibold">Tick the worries you can do something about.</p>
    <ul className="mt-3 space-y-2">
      {items.map((w, i) => (
        <li key={`${i}-${w}`}><label className="flex items-start gap-3 mm-soft rounded-2xl px-4 py-3 cursor-pointer"><Checkbox checked={mine.has(i)} onCheckedChange={() => toggle(i)} className="mt-0.5" data-testid={`tool-worry-item-${i}`} /><span className="break-words [overflow-wrap:anywhere]">{w}</span></label></li>
      ))}
    </ul>
    <button onClick={onDone} className="mm-btn mt-4" data-testid="tool-worry-sorted">See my two lists</button>
  </div>
);

const Lists = ({ items, mine, tool, onAgain }) => {
  const [mineList, restList] = useMemo(() => {
    const sorted = items.map((w, i) => ({ w, key: `${i}-${w}`, mine: mine.has(i) }));
    return [sorted.filter((x) => x.mine), sorted.filter((x) => !x.mine)];
  }, [items, mine]);
  return (
  <div className="mm-fade-up" data-testid="tool-worry-result">
    <div className="grid sm:grid-cols-2 gap-3">
      <div className="rounded-3xl p-4 bg-[#E9F6EC] text-[#24452D]"><h3 className="font-bold">In my control</h3><p className="text-xs opacity-75 mb-2">Pick one and choose a tiny next step.</p>
        <ul className="space-y-1 text-sm" data-testid="tool-worry-mine">{mineList.map((x) => <li key={x.key} className="break-words">✅ {x.w}</li>)}</ul></div>
      <div className="rounded-3xl p-4 bg-[#F3EEFC] text-[#3A2E4F]"><h3 className="font-bold">Can rest for now</h3><p className="text-xs opacity-75 mb-2">Notice them, name them, and let them be.</p>
        <ul className="space-y-1 text-sm" data-testid="tool-worry-rest">{restList.map((x) => <li key={x.key} className="break-words">☁️ {x.w}</li>)}</ul></div>
    </div>
    <p className="text-xs text-mm-muted mt-3">Nothing you wrote was saved or sent anywhere.</p>
    <ToolInvite tool={tool} onAgain={onAgain} card={{ line: "I emptied my head for 2 minutes 📝", sub: "Worry dump. Try it free.", emoji: "📝" }} />
  </div>
  );
};

export const WorryTool = ({ tool }) => {
  const [stage, setStage] = useState("ready");
  const [text, setText] = useState("");
  const [mine, setMine] = useState(new Set());
  const items = text.split("\n").map((l) => l.trim()).filter(Boolean).slice(0, 40);
  const toggle = (i) => setMine((s) => { const n = new Set(s); if (n.has(i)) n.delete(i); else n.add(i); return n; });
  const begin = () => { resetTrack(tool.slug); setText(""); setMine(new Set()); setStage("write"); track(tool.slug, "start"); };
  if (stage === "ready") return (
    <div className="text-center"><p className="text-mm-muted mb-5">For 2 minutes, write down everything on your mind. Nothing is saved.</p>
      <button onClick={begin} className="mm-btn !px-8 !py-3.5 text-lg" data-testid="tool-start"><Play size={18} />Start</button></div>
  );
  if (stage === "write") return <Writing text={text} setText={setText} onDone={() => setStage("sort")} />;
  if (stage === "sort") return <Sort items={items} mine={mine} toggle={toggle} onDone={() => { setStage("done"); track(tool.slug, "finish"); }} />;
  return <Lists items={items} mine={mine} tool={tool} onAgain={begin} />;
};
